<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { WizardField, WizardFieldType, WizardFieldOptionPayload } from '@/types/wizard-forms'
import { useWizardFields } from '@/composables/useWizardFields'
import WizardFieldOptionEditor from './WizardFieldOptionEditor.vue'
import AppSpinner from '@/components/shared/AppSpinner.vue'

const props = defineProps<{
  field: WizardField
  formId: number
  pageId: number
}>()

const emit = defineEmits<{
  saved: []
  deleted: []
  cancel: []
}>()

const { t } = useI18n()
const { updateField, deleteField } = useWizardFields()

const OPTION_TYPES: WizardFieldType[] = ['checkbox_group', 'radio_group', 'select']
const ALL_TYPES: WizardFieldType[] = [
  'text',
  'textarea',
  'number',
  'date_picker',
  'checkbox',
  'checkbox_group',
  'radio_group',
  'select',
]

// Form state
const label = ref(props.field.label)
const placeholder = ref(props.field.placeholder ?? '')
const helpText = ref(props.field.help_text ?? '')
const isRequired = ref(props.field.is_required)
const gridRow = ref(String(props.field.grid_row))
const gridColumn = ref(String(props.field.grid_column))
const type = ref<WizardFieldType>(props.field.type)
const pendingOptions = ref<WizardFieldOptionPayload[]>(
  props.field.options?.map((o) => ({
    id: o.id,
    label: o.label,
    value: o.value,
    extra_cost: parseFloat(o.extra_cost),
    sort_order: o.sort_order,
  })) ?? [],
)
const extraCost = ref(props.field.extra_cost ?? '')

watch(
  () => props.field,
  (f) => {
    label.value = f.label
    placeholder.value = f.placeholder ?? ''
    helpText.value = f.help_text ?? ''
    isRequired.value = f.is_required
    gridRow.value = String(f.grid_row)
    gridColumn.value = String(f.grid_column)
    type.value = f.type
    extraCost.value = f.extra_cost ?? ''
    pendingOptions.value =
      f.options?.map((o) => ({
        id: o.id,
        label: o.label,
        value: o.value,
        extra_cost: parseFloat(o.extra_cost),
        sort_order: o.sort_order,
      })) ?? []
  },
)

const isOptionType = computed(() => OPTION_TYPES.includes(type.value))

const saving = ref(false)
const saveErrors = ref<Record<string, string[]>>({})
const generalError = ref('')

const confirmDelete = ref(false)
const deleting = ref(false)

function isAxiosError(
  e: unknown,
): e is { response: { status: number; data: { errors?: Record<string, string[]>; message?: string } } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: unknown }).response !== null
  )
}

async function handleSave(): Promise<void> {
  saveErrors.value = {}
  generalError.value = ''
  saving.value = true

  try {
    await updateField(props.formId, props.pageId, props.field.id, {
      type: type.value,
      label: label.value,
      placeholder: placeholder.value.trim() === '' ? null : placeholder.value,
      help_text: helpText.value.trim() === '' ? null : helpText.value,
      is_required: isRequired.value,
      grid_row: parseInt(gridRow.value, 10),
      grid_column: parseInt(gridColumn.value, 10),
      extra_cost: type.value === 'checkbox' && extraCost.value !== '' ? parseFloat(extraCost.value) : null,
      options: isOptionType.value ? pendingOptions.value : undefined,
    })
    emit('saved')
  } catch (e: unknown) {
    if (isAxiosError(e) && e.response.status === 422 && e.response.data.errors) {
      saveErrors.value = e.response.data.errors
    } else {
      generalError.value = t('common.error_generic')
    }
  } finally {
    saving.value = false
  }
}

async function handleDelete(): Promise<void> {
  deleting.value = true
  try {
    await deleteField(props.formId, props.pageId, props.field.id)
    emit('deleted')
  } catch {
    generalError.value = t('common.error_generic')
    confirmDelete.value = false
  } finally {
    deleting.value = false
  }
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- General error -->
    <div
      v-if="generalError"
      class="rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400"
    >
      {{ generalError }}
    </div>

    <!-- Field Type -->
    <div>
      <label class="block text-xs font-medium text-slate-400 mb-1">
        {{ t('wizard_forms.field_editor.type_label') }}
      </label>
      <select
        v-model="type"
        class="w-full rounded-lg bg-slate-700/50 border border-slate-600 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
      >
        <option v-for="ft in ALL_TYPES" :key="ft" :value="ft">
          {{ t(`wizard_forms.field_types.${ft}`) }}
        </option>
      </select>
      <p v-if="saveErrors.type" class="mt-1 text-xs text-red-400">{{ saveErrors.type[0] }}</p>
    </div>

    <!-- Label -->
    <div>
      <label class="block text-xs font-medium text-slate-400 mb-1">
        {{ t('wizard_forms.field_editor.label_label') }}
        <span class="text-red-400">*</span>
      </label>
      <input
        v-model="label"
        type="text"
        class="w-full rounded-lg bg-slate-700/50 border px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        :class="saveErrors.label ? 'border-red-500' : 'border-slate-600'"
      />
      <p v-if="saveErrors.label" class="mt-1 text-xs text-red-400">{{ saveErrors.label[0] }}</p>
    </div>

    <!-- Placeholder -->
    <div>
      <label class="block text-xs font-medium text-slate-400 mb-1">
        {{ t('wizard_forms.field_editor.placeholder_label') }}
      </label>
      <input
        v-model="placeholder"
        type="text"
        class="w-full rounded-lg bg-slate-700/50 border border-slate-600 px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
      />
    </div>

    <!-- Help text -->
    <div>
      <label class="block text-xs font-medium text-slate-400 mb-1">
        {{ t('wizard_forms.field_editor.help_text_label') }}
      </label>
      <textarea
        v-model="helpText"
        rows="2"
        class="w-full rounded-lg bg-slate-700/50 border border-slate-600 px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
      />
    </div>

    <!-- Grid position -->
    <div class="grid grid-cols-2 gap-3">
      <div>
        <label class="block text-xs font-medium text-slate-400 mb-1">
          {{ t('wizard_forms.field_editor.grid_row_label') }}
          <span class="text-red-400">*</span>
        </label>
        <input
          v-model="gridRow"
          type="number"
          min="1"
          class="w-full rounded-lg bg-slate-700/50 border px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
          :class="saveErrors.grid_row ? 'border-red-500' : 'border-slate-600'"
        />
        <p v-if="saveErrors.grid_row" class="mt-1 text-xs text-red-400">{{ saveErrors.grid_row[0] }}</p>
      </div>
      <div>
        <label class="block text-xs font-medium text-slate-400 mb-1">
          {{ t('wizard_forms.field_editor.grid_column_label') }}
          <span class="text-red-400">*</span>
        </label>
        <input
          v-model="gridColumn"
          type="number"
          min="1"
          max="12"
          class="w-full rounded-lg bg-slate-700/50 border px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
          :class="saveErrors.grid_column ? 'border-red-500' : 'border-slate-600'"
        />
        <p v-if="saveErrors.grid_column" class="mt-1 text-xs text-red-400">{{ saveErrors.grid_column[0] }}</p>
      </div>
    </div>

    <!-- Required toggle -->
    <label class="flex items-center gap-3 cursor-pointer">
      <input
        v-model="isRequired"
        type="checkbox"
        class="w-4 h-4 rounded border-slate-600 bg-slate-700 text-emerald-500 focus:ring-emerald-500"
      />
      <span class="text-sm text-slate-300">{{ t('wizard_forms.field_editor.is_required_label') }}</span>
    </label>

    <!-- Extra cost (only for checkbox type) -->
    <div v-if="type === 'checkbox'">
      <label class="block text-xs font-medium text-slate-400 mb-1">
        {{ t('wizard_forms.field_editor.extra_cost_label') }}
      </label>
      <input
        v-model="extraCost"
        type="number"
        min="0"
        step="0.01"
        :placeholder="t('wizard_forms.field_editor.extra_cost_placeholder')"
        class="w-full rounded-lg bg-slate-700/50 border px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        :class="saveErrors.extra_cost ? 'border-red-500' : 'border-slate-600'"
      />
      <p class="mt-1 text-xs text-slate-500">{{ t('wizard_forms.field_editor.extra_cost_hint') }}</p>
      <p v-if="saveErrors.extra_cost" class="mt-1 text-xs text-red-400">{{ saveErrors.extra_cost[0] }}</p>
    </div>

    <!-- Options editor (only for option-based types) -->
    <WizardFieldOptionEditor
      v-if="isOptionType"
      :options="props.field.options ?? []"
      @update="(opts) => { pendingOptions = opts }"
    />
    <p
      v-if="isOptionType && saveErrors['options']"
      class="text-xs text-red-400"
    >
      {{ saveErrors['options'][0] }}
    </p>

    <!-- Actions -->
    <div class="flex items-center gap-2 pt-2 border-t border-slate-700">
      <button
        type="button"
        :disabled="saving"
        class="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
        @click="handleSave"
      >
        <AppSpinner v-if="saving" size="sm" />
        {{ saving ? t('common.loading') : t('common.save') }}
      </button>

      <button
        type="button"
        class="inline-flex items-center border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-medium rounded-lg px-4 py-2 text-sm transition-colors"
        @click="emit('cancel')"
      >
        {{ t('common.cancel') }}
      </button>

      <div class="flex-1" />

      <template v-if="!confirmDelete">
        <button
          type="button"
          class="inline-flex items-center text-xs text-red-400 hover:text-red-300 border border-red-500/30 hover:border-red-500/60 rounded-lg px-3 py-2 transition-colors"
          @click="confirmDelete = true"
        >
          {{ t('common.delete') }}
        </button>
      </template>
      <template v-else>
        <span class="text-xs text-slate-400">{{ t('wizard_forms.field_editor.confirm_delete') }}?</span>
        <button
          type="button"
          :disabled="deleting"
          class="text-xs font-medium text-red-400 hover:text-red-300 border border-red-500/30 hover:border-red-500/60 rounded-lg px-3 py-2 disabled:opacity-50 transition-colors"
          @click="handleDelete"
        >
          {{ deleting ? t('common.loading') : t('common.confirm') }}
        </button>
        <button
          type="button"
          class="text-xs text-slate-400 hover:text-white border border-slate-600 rounded-lg px-3 py-2 transition-colors"
          @click="confirmDelete = false"
        >
          {{ t('common.cancel') }}
        </button>
      </template>
    </div>
  </div>
</template>
